import Swiper from 'swiper/bundle';
import { SELECTORS, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Solutions Module
 */
export default class SolutionsModule extends MessageBus {
  /**
   * Constructor function of the SolutionsModule class. Must receive one argument, a DOM
   * node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {SolutionsModule} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {SolutionsModule} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    // Store the DOM element directly, scoped to THIS component instance
    this.sliderEl = this.element.querySelector(
      `.${SELECTORS.SOLUTIONS_MODULE_SLIDER}`
    );

    this.breakpoints = {
      desktop: SELECTORS.MAX_DESKTOP
    };

    this.swiperConfig = {
      slidesPerView: 1.15,
      spaceBetween: 15,
      grabCursor: true,
      breakpoints: {
        1024: {
          slidesPerView: 1.5,
          spaceBetween: 15
        }
      }
    };

    this.swiperInstance = null;
    this.mediaQuery = window.matchMedia(this.breakpoints.desktop);

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {SolutionsModule} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleBreakpointChange = this.onBreakpointChange.bind(this);

    return this;
  }

  /**
   * Create event listeners to enable interaction with view
   * @returns {SolutionsModule} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.mediaQuery.addEventListener(
      EVENTS.CHANGE,
      this.handleBreakpointChange
    );
    this.onBreakpointChange();

    return this;
  }

  /**
   * Initializes Swiper on mobile, destroys it on dekstop and above.
   * @returns {SolutionsModule} A reference to the current instance of the class
   */
  onBreakpointChange() {
    if (this.mediaQuery.matches && !this.swiperInstance) {
      this.swiperInstance = new Swiper(this.sliderEl, this.swiperConfig);
    } else if (!this.mediaQuery.matches && this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;

      this.sliderEl
        ?.querySelector(`.${SELECTORS.SWIPER_WRAPPER}`)
        ?.removeAttribute(SELECTORS.STYLE);
      this.sliderEl
        ?.querySelectorAll(`.${SELECTORS.SWIPER_SLIDE}`)
        .forEach((slide) => slide.removeAttribute(SELECTORS.STYLE));
    }

    return this;
  }
}
